export const EFFECTIVE_DATE = 'August 5, 2026';
export const LAST_UPDATED = 'August 5, 2026';

export const INTRO =
  'Nothing To Wear is a peer-to-peer marketplace connecting people who lend or sell fashion items ("Lenders" and "Sellers") with people who rent or buy them ("Renters" and "Buyers"). This Renter & Buyer Policy explains, in practical terms, what you can expect, what’s expected of you, and how liability is handled when you rent or buy through the Platform. It supplements — and should be read alongside — our Terms and Conditions and Privacy Policy, and forms part of the agreement you accept when you use the Platform. Capitalized terms not defined here have the meaning given in our Terms and Conditions.';

export const SECTIONS = [
  {
    number: '1',
    title: 'Who This Policy Covers',
    blocks: [
      {
        p:
          'This Policy applies to you whenever you rent or buy an item through the Platform, whether you are browsing, booking, checking out, receiving an item, or returning it. A separate Lender & Seller Policy applies to you when you are the one listing an item.',
      },
    ],
  },
  {
    number: '2',
    title: 'Eligibility',
    blocks: [
      {
        p:
          'To rent or buy on the Platform, you must be at least 18 years old, have reached the age of majority in your province or territory of residence, be able to receive delivery or arrange pickup at a Canadian address, and have a valid payment method on file with our payment processor, Stripe. By completing a booking or purchase, you confirm you meet these requirements.',
      },
    ],
  },
  {
    number: '3',
    title: 'Payment Through Stripe',
    blocks: [
      {
        p:
          'All payments are processed through Stripe, a third-party payment processor. When you add a payment method, your card details are sent directly to Stripe and are never stored on our servers. Stripe is a Level 1 PCI DSS-compliant payment processor, which is the highest level of certification in the payment card industry. Charges and refunds all run through Stripe under its own terms of service, which you also agree to when you add a payment method.',
      },
      { p: 'All prices on the Platform are shown in Canadian dollars (CAD).' },
    ],
  },
  {
    number: '4',
    title: "What You Pay, and What's Included",
    blocks: [
      { p: 'Before you confirm a booking or purchase, the checkout screen will clearly itemize:' },
      {
        list: [
          "the item's rental fee or sale price, as set by the Lender/Seller;",
          'applicable taxes (GST and, where applicable, provincial sales tax);',
          'the Buyer Protection Fee, a mandatory 5% charge on the rental price, applied to every rental; and',
          'shipping cost, if applicable, or confirmation that the item is available for local pickup.',
        ],
      },
      {
        p:
          'Nothing To Wear earns its revenue through a 20% commission charged to the Lender/Seller, deducted from their payout — this is not an additional charge added on top of the price you see. The price displayed to you at checkout is the total amount you pay for the item itself, plus the Buyer Protection Fee, before taxes.',
      },
    ],
  },
  {
    number: '5',
    title: 'Renting an Item: Your Rights and Responsibilities',
    blocks: [
      { h3: '5.1 What You Can Expect' },
      {
        p:
          'You have the right to receive the item described and photographed in the Listing, in the condition stated, by the agreed delivery or pickup time, for the full Rental Period you booked.',
      },
      { h3: '5.2 Your Responsibilities' },
      {
        list: [
          'inspect the item and take photos when you receive it, and report any pre-existing issue to help@nothingtowearclub.ca the same day (see Section 6);',
          'use the item only for its intended purpose and follow any care instructions in the Listing;',
          'not alter, professionally clean (unless instructed), or attempt to repair the item yourself;',
          'not sublet, lend, or permit anyone else to use the item unless the Lender agrees; and',
          'return the item by the end of the Rental Period, using the return method specified in the Listing, and take photos of its condition when you hand it back (see Section 6).',
        ],
      },
    ],
  },
  {
    number: '6',
    title: 'Check-In and Check-Out: Inspecting and Reporting Issues',
    blocks: [
      {
        p:
          "For your protection and the Lender's, we strongly recommend that both of you fully inspect the item and take photos at check-out (when you receive it) and again at check-in (when you return it), and upload those photos to the in-app messaging thread for that booking. A clear photo record from both ends is the fastest way to resolve a disagreement about an item's condition.",
      },
      {
        p:
          "If a problem comes up, please try to resolve it directly with the Lender through in-app messaging first. If you can't reach a resolution together, contact us at help@nothingtowearclub.ca and we'll help sort it out.",
      },
      {
        list: [
          "Didn't receive the item? Email help@nothingtowearclub.ca immediately, the same day the rental was scheduled to begin.",
          'Item arrives damaged, or not as described? Email help@nothingtowearclub.ca the same day you receive it, with photos.',
          "Returning the item late? A late fee applies automatically under Section 9 — there's nothing separate to report.",
          "If the Lender reports that you returned the item damaged, they're required to do so, with photos, the same day it's returned; we'll let you know and you can respond with your own evidence.",
        ],
      },
    ],
  },
  {
    number: '7',
    title: 'Buying an Item: Your Rights and Responsibilities',
    blocks: [
      { h3: '7.1 Condition' },
      {
        p:
          "Unless a Listing states an item is new with tags, you are buying a pre-owned item in the condition described and photographed. Sellers are required to disclose material flaws they're aware of.",
      },
      { h3: '7.2 When a Sale Is Final' },
      {
        p:
          "Purchases are generally final once you've had a reasonable opportunity to inspect the item, except where the item is materially not as described (Section 10), or where a return right applies under the BPCPA or another applicable law, or was voluntarily offered by the Seller and shown to you at checkout.",
      },
      { h3: '7.3 Your Right to Cancel Before Delivery' },
      {
        p:
          "Where required under BC's distance-sales rules, you have the right to cancel an online purchase in the circumstances set out in the BPCPA (for example, where the goods are not delivered within the timeframe disclosed, or the Seller misrepresented a material term of the contract). Contact us if you believe this applies to your order.",
      },
    ],
  },
  {
    number: '8',
    title: 'Cancellations',
    blocks: [
      {
        p:
          "If you need to cancel a booking or order, contact us directly at help@nothingtowearclub.ca and we'll take care of it for you — cancellations aren't handled automatically through checkout.",
      },
      {
        p:
          'If a Lender or Seller cancels a confirmed booking or order themselves for a reason not attributable to you, you will receive a full refund.',
      },
    ],
  },
  {
    number: '9',
    title: 'Damage, Loss, and Your Liability as a Renter',
    blocks: [
      {
        p:
          'You are responsible for a rented item from the moment you receive it until the Lender confirms its return. If an item is lost, stolen while in your care, or damaged beyond ordinary wear, you may be charged repair costs, cleaning costs, or the item’s replacement value as determined by Nothing To Wear Club Inc., up to the cap below.',
      },
      {
        boldLead: 'Liability cap:',
        p:
          ' Unless you caused loss or damage intentionally or through gross negligence, your total liability for a single rental for damage or loss is capped at the replacement value. This cap applies to repair, cleaning, and replacement-value charges for damage or loss. Late fees for a delayed return are calculated and capped separately, as set out below, and are charged in addition to — not limited by — this cap.',
      },
      {
        p:
          'The Buyer Protection Fee (Section 4) applies automatically to every rental and covers minor accidental damage to a garment beyond ordinary wear; it does not excuse or cover larger accidental damage, which remains subject to the Liability cap above.',
      },
      {
        boldLead: 'Late return fee:',
        p:
          ' Rental Periods run on a day basis (midnight to midnight), as shown at checkout. If you do not return the item by the end of the last day of your Rental Period, you will be charged a late fee equal to 2× (two times) the item’s daily rental rate for each calendar day the item remains outstanding beyond the Rental Period. The late fee is capped at a maximum of 200% of the item’s replacement value and is charged in addition to, and does not reduce, any separate damage or loss charges under this Section. Your maximum possible combined exposure for a single rental — damage/loss charges plus late fees — is therefore the sum of the Liability cap above and this 200% late-fee cap.',
      },
    ],
  },
  {
    number: '10',
    title: 'Filing a Claim (Not as Described, Damage Disputes)',
    blocks: [
      {
        p:
          "If an item you bought is materially different from its Listing, or you believe a damage or late-return charge against you is unwarranted, first try to resolve it directly with the Lender/Seller through in-app messaging, using the photos from Section 6. If you can't reach a resolution together, email help@nothingtowearclub.ca within 3 business days of delivery or the disputed charge, including photos and any relevant messages. We will review evidence from both sides and aim to respond within 5 business days with a refund, partial refund, replacement, or a decision upholding the charge. This process doesn't limit your right to pursue other remedies available under BC or federal law.",
      },
    ],
  },
  {
    number: '11',
    title: 'Payment Disputes and Chargebacks',
    blocks: [
      {
        p:
          "You're always entitled to dispute a charge with your card issuer. Because doing so can freeze funds meant for the Lender/Seller and trigger fees from Stripe, we ask that you try our in-app resolution process first — most issues are resolved faster that way. Repeated chargebacks found to be unwarranted (for example, disputing a charge for an item you received and kept, without first raising the issue with us) may result in account restrictions.",
      },
    ],
  },
  {
    number: '12',
    title: "Conduct We Don't Allow",
    blocks: [
      {
        list: [
          'arranging payment or exchange outside the Platform to avoid fees or protections;',
          'providing false information to obtain a refund or damage-claim payout;',
          'using a rented item for a purpose inconsistent with the Listing (e.g., commercial photoshoots for an item listed for personal wear, if not disclosed and agreed); and',
          'harassing, discriminating against, or pressuring a Lender or Seller.',
        ],
      },
    ],
  },
  {
    number: '13',
    title: 'Our Liability to You',
    blocks: [
      {
        p:
          'Nothing To Wear is a marketplace, not a party to your rental or purchase contract with the Lender/Seller (see our Terms and Conditions, Section 4). To the fullest extent permitted by law, and except for a right that cannot be limited under the BPCPA or other applicable consumer-protection law, our total liability to you for a claim relating to a specific Transaction is capped at the greater of (a) the fees we received from you in connection with that Transaction, or (b) CAD $100 (see Terms and Conditions, Section 17.2, which this mirrors).',
      },
    ],
  },
  {
    number: '14',
    title: 'Your Consumer Protection Rights in British Columbia',
    blocks: [
      {
        p:
          "As a consumer, you have rights under the BPCPA that we can't contract away, including the right to receive clear, itemized pricing before you pay, and — depending on the circumstances — cancellation rights for online purchases. If a dispute with us can't be resolved informally, you can bring it to the BC Civil Resolution Tribunal or the applicable BC court, and nothing in our Terms requires you to arbitrate a claim of $5,000 or less, or bars you from a class proceeding for such a claim. See Section 20 of our Terms and Conditions for full detail.",
      },
    ],
  },
  {
    number: '15',
    title: 'Privacy',
    blocks: [
      {
        p:
          "Information about how we collect, use, and share your personal information — including what's shared with a Lender/Seller to complete a Transaction — is set out in our Privacy Policy.",
      },
    ],
  },
  {
    number: '16',
    title: 'Changes to This Policy',
    blocks: [
      {
        p:
          'We may update this Policy from time to time. Material changes will be communicated in advance, consistent with the approach described in our Terms and Conditions.',
      },
    ],
  },
];

export const CONTACT = {
  number: '17',
  title: 'Contact Us',
  companyLine: 'Nothing To Wear Club Inc. (operating as "Nothing To Wear")',
  email: 'help@nothingtowearclub.ca',
  address: 'Unit #30, 5300 Main St, Kelowna, BC V1W 3W4',
};
